import { combineReducers } from 'redux';
import affiliate, { Affiliate, getTrsTrackId } from './affiliate';
import domain, * as fromDomain from './domain';
import * as fromDomainField from './domain-field';
import intent, * as fromIntent from './intent';
import websiteCreation, * as fromWebsiteCreation from './website-creation';
import options, * as fromOptions from './options';
import pricing, * as fromPricing from './pricing';
import experiments, * as fromExperiments from './experiments';
import template, * as fromTemplate from './template';
import error from './error';
import user, * as fromUser from './user';
import { APP_PATHS } from '../../routes/route-helpers';

export interface AppState {
  affiliate: Affiliate;
  domain: fromDomain.DomainState;
  error: string;
  experiments: fromExperiments.Experiments;
  intent: fromIntent.Intent;
  options: fromOptions.Options;
  pricing: fromPricing.PricingState;
  template: fromTemplate.TemplateState;
  user: fromUser.UserState;
  websiteCreation: fromWebsiteCreation.WebsiteCreation;
}

export const getUsername = (state: AppState): string => {
  if (!state) {
    return null;
  }
  return fromDomain.getFieldValue(state.domain, fromDomainField.FREE);
};

export const getLang = (state: AppState): string => {
  if (!state) {
    return null;
  }
  return fromOptions.getLang(state.options);
};

export const getTemplateSubtype = (state: AppState): string => {
  if (!state) {
    return null;
  }
  return fromTemplate.getSubtype(state.template);
};

export const shouldSkipIndustryView = (state: AppState): boolean => {
  if (!state) {
    return false;
  }
  return fromIntent.shouldSkipIndustryView(state.intent);
};

export const shouldSkipTemplateView = (state: AppState): boolean => {
  if (!state) {
    return false;
  }
  return fromTemplate.shouldSkipTemplateView(state.template);
};

export const shouldSkipPricingView = (state: AppState): boolean => {
  if (!state) {
    return false;
  }
  return fromPricing.shouldSkipPricingView(state.pricing);
};

export const shouldjumpToIndustryView = (state: AppState): boolean => {
  if (!state) {
    return false;
  }

  return fromIntent.shouldjumpToIndustryView(state.intent);
};

export const getSelectedPackage = (state: AppState): string => {
  if (!state) {
    return null;
  }
  return fromPricing.getSelectedPackage(state.pricing);
};

export const paidPackageIsSelected = (state: AppState): boolean => {
  if (!state) {
    return false;
  }
  return fromPricing.paidPackageIsSelected(state.pricing);
};

export const getEasyMarketingTrackId = (state: AppState): string => {
  if (!state) {
    return null;
  }
  return getTrsTrackId(state.affiliate);
};

export const shouldForwardToCheckout = (state: AppState, nextDestination: string): boolean => {
  if (!state) {
    return false;
  }
  const isNextDestinationDomain = nextDestination === APP_PATHS.DOMAIN;
  return isNextDestinationDomain && paidPackageIsSelected(state);
};

export const shouldForwardToDolphinCms = (state: AppState, nextDestination: string): boolean => {
  if (!state) {
    return false;
  }

  return (
    (fromIntent.shouldForwardToDolphinCms(state.intent) &&
      nextDestination === APP_PATHS.INDUSTRY) ||
    (fromIntent.shouldForwardToDolphinCms(state.intent) && nextDestination === APP_PATHS.PRODUCT)
  );
};

// TODO cleanup GRW-1410
export const shouldForwardToOnestop = (state: AppState, nextDestination: string): boolean => {
  if (!state) {
    return false;
  }

  return fromIntent.shouldForwardToOnestop(state.intent);
};

export const shouldPollLoginLink = (state: AppState): boolean => {
  if (!state) {
    return false;
  }
  return fromWebsiteCreation.shouldPoll(state.websiteCreation);
};

export const websiteCreationWasRequested = (state: AppState): boolean => {
  if (!state) {
    return false;
  }
  return fromWebsiteCreation.wasRequested(state.websiteCreation);
};

export const websiteCreationHasFailedOrIsTooOld = (state: AppState): boolean => {
  if (!state) {
    return false;
  }
  return fromWebsiteCreation.hasFailedOrIsTooOld(state.websiteCreation);
};

export const websiteIsReady = (state: AppState): boolean => {
  if (!state) {
    return false;
  }
  return fromWebsiteCreation.isReady(state.websiteCreation);
};

export const isMobile = (state: AppState): boolean => {
  if (!state) {
    return false;
  }
  return fromOptions.isMobile(state.options);
};

export const isSignupWithBackgroundExperiment = (state: AppState): boolean => {
  if (!state) {
    return null;
  }
  return fromExperiments.isSignupWithBackgroundExperiment(state.experiments);
};

export const isSignupWithGiveMeFiveExperiment = (state: AppState): boolean => {
  if (!state) {
    return null;
  }
  return fromExperiments.isSignupWithGiveMeFiveExperiment(state.experiments);
};

export const isSignupWithGiveMeFiveDescExperiment = (state: AppState): boolean => {
  if (!state) {
    return null;
  }
  return fromExperiments.isSignupWithGiveMeFiveDescExperiment(state.experiments);
};

export const isSignupWithThreeVersusFive = (state: AppState): boolean => {
  if (!state) {
    return null;
  }
  return fromExperiments.isSignupWithThreeVersusFive(state.experiments);
};

export const isSignupWithProductNaming = (state: AppState): boolean => {
  if (!state) {
    return null;
  }
  return fromExperiments.isSignupWithProductNaming(state.experiments);
};

export default combineReducers({
  affiliate,
  domain,
  error,
  intent,
  options,
  pricing,
  experiments,
  template,
  user,
  websiteCreation,
});
